import { useState } from "react";
import { Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

export default function TagExtractor() {
  useSeo("YouTube Tag Extractor", "Extract the exact tags and keywords from any public YouTube video for free.");
  const [url, setUrl] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const extract = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    try {
      const res = await api.get("/tags", { params: { url } });
      setData(res.data);
      if (!res.data.tags.length) toast.info("This video has no public tags");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="tag-extractor" title="Tag Extractor" tagline="See exactly which tags a video ranks with. Paste a link, copy the whole set." hasResults={!!data}>
      <form onSubmit={extract} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Video URL</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="tags-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="tags-extract-btn"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <Tag size={16} weight="bold" /> {loading ? "Extracting..." : "Extract tags"}
          </button>
        </div>
      </form>

      {data && (
        <div className="mt-8 surface rounded-[20px] p-6 sm:p-8 fade-up">
          <div className="flex items-start gap-4 mb-6">
            {data.thumbnail && <img src={data.thumbnail} alt="" className="w-28 rounded-[10px] aspect-video object-cover" />}
            <div className="min-w-0">
              <h2 className="font-bold text-base truncate" data-testid="tags-video-title">{data.title}</h2>
              <p className="text-[var(--muted)] text-sm">{data.channelTitle}{data.viewCount ? ` · ${Number(data.viewCount).toLocaleString()} views` : ""}</p>
            </div>
          </div>
          {data.tags.length > 0 ? (
            <>
              <div className="flex flex-wrap gap-2" data-testid="tags-list">
                {data.tags.map((t) => (
                  <span key={t} className="px-3.5 py-1.5 rounded-full bg-[var(--glow)]/12 border border-[var(--glow)]/30 text-sm">
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-6">
                <CopyButton text={data.tags.join(", ")} label={`Copy all ${data.tags.length} tags`} testId="copy-all-tags-btn" />
              </div>
            </>
          ) : (
            <p className="text-[var(--muted)] text-sm">No public tags on this video.</p>
          )}
        </div>
      )}
    </ToolShell>
  );
}
